<template>
  <base-modal
    :eager="true"
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="attachment.description"
          v-bind:label="$t('Description')"
          :error-messages="descriptionErrors"
          @input="$v.attachment.description.$touch()"
          @blur="$v.attachment.description.$touch()"
        ></v-text-field>
      </v-col>
      <v-col cols="12" v-show="attachment.id == 0">
        <VueDropzone v-if="dropzoneOptions.url"
          id="dropZoneAttachmentModal"
          :ref="dropZoneKey"
          :options="dropzoneOptions"
          @vdropzone-success="onFileSuccess"
          @vdropzone-file-added="onFileAdded"
          @vdropzone-removed-file="onFileRemoved"
          @vdropzone-error="onFileError"
        />
      </v-col>
    </v-row>
  </base-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import AttachmentService from '@/services/AttachmentService';
import DocumentService from '@/services/DocumentService';
import Attachment from '@/ecm/models/search/results/list/Attachment';
import Utils from '@/ecm/common/Utils';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import ContentActionType from '../../../../../../../ecm/models/ContentActionType';

export default {
    components: {
        VueDropzone: vue2Dropzone,
    },
    props: {
        contentTypeId: {
            type: Number,
        },
    },
    created() {
        DocumentService.getSupportedFileFormats().then((data) => {
            this.dropzoneOptions.acceptedFiles = data.join(',');
        });
    },
    data() {
        return {
            attachment: new Attachment(),
            modal: {
                title: this.$t('DocumentAttachments'),
                key: 'attachmentModal',
            },
            open: false,
            dropZoneKey: 'drop-zone-attachment-modal',
            dropzoneOptions: {
                url: '',
                thumbnailWidth: 150,
                timeout: 30000, //30 seconds
                maxFilesize: 100, //100 MB
                maxFiles: 1,
                destroyDropzone: true,
                headers: { Authorization: 'bearer ' + this.$store.getters['account/token'] },
                addRemoveLinks: true,
                acceptedFiles: '',
                dictDefaultMessage: this.$t('DropzoneMessage'),
                dictRemoveFile: this.$t('Remove'),
                dictInvalidFileType: this.$t('DropzoneInvalidFileType'),
            },
        };
    },
    mixins: [validationMixin],
    validations: {
        attachment: {
            description: { required },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        descriptionErrors() {
            const errors = [];
            if (!this.$v.attachment.description.$dirty) return errors;
            !this.$v.attachment.description.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        dropZone() {
            return this.$refs[this.dropZoneKey];
        },
    },
    mounted() {
        this.updateDropzoneUrl();
    },
    methods: {
        updateDropzoneUrl() {
            if (!this.contentTypeId) return;
            const url = `${this.$store.getters['account/apiUrl']}/api/document/upload-attachment?contentTypeID=${this.contentTypeId}&contentActionType=${ContentActionType.AddAttachment}`;

            this.dropzoneOptions.url = url;

            this.$nextTick(() => {
                const dropzoneComponent = this.$refs[this.dropZoneKey];
                if (dropzoneComponent && dropzoneComponent.dropzone) {
                    dropzoneComponent.dropzone.options.url = url;
                }
            });
        },
        onFileSuccess(file, response) {
            this.attachment.fileId = response[0].id;
            this.attachment.fileName = file.name;
            if (!this.attachment.description) {
                this.attachment.description = file.name;
            }
        },
        onFileAdded(file) {
            if (Utils.text.contains(this.attachment.fileName, file.name) > 0) {
                Utils.toast.error(this.$t('DocumentDuplicatedError'));
                this.dropZone.removeFile(file);
            }

            var extension = `.${file.name.split('.').pop()}`.toUpperCase();

            if (
                this.dropzoneOptions.acceptedFiles
                    .split(',')
                    .filter((f) => f == extension).length == 0
            ) {
                Utils.toast.error(this.$t('DropzoneInvalidFileType'));
                this.dropZone.removeFile(file);
            }
        },
        onFileRemoved(file) {
            this.attachment.fileId = '';
            this.attachment.fileName = '';
        },
        onFileError(file) {
            if (!file.accepted) {
                this.dropZone.removeFile(file);
            }
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if (this.attachment.id <= 0 && !this.attachment.fileId) {
                Utils.toast.error(this.$t('DocumentAttachmentRequired'));
                return;
            }

            this.modalInstance.progress(true);

            AttachmentService.save(this.attachment.toDto())
                .then((result) => {
                    if (result) {
                        this.hide();
                        this.$emit('on-attachment-saved');
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        new(documentId, files, contentTypeId) {
            this.$v.$reset();
            this.attachment = new Attachment();
            this.attachment.documentId = documentId;

            this.contentTypeId = contentTypeId;
            this.updateDropzoneUrl();

            this.$nextTick(() => {
                this.dropZone.removeAllFiles();
                if (files && files.length > 0) {
                    this.dropZone.dropzone.handleFiles([files[0]]);
                }
                this.show();
            });
        },
        edit(attachment) {
            this.attachment = Utils.object.clone(new Attachment(), attachment);
            this.$nextTick(() => {
                this.dropZone.removeAllFiles();
                this.show();
            });
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
        },
    },
};
</script>
